/**
 * Domain utility functions for cookie management
 * Separated to avoid circular dependencies
 */

/**
 * Encode domain to a safe cookie name suffix
 * Converts domain like ".qa.lendi.pl" to "qa-lendi-pl"
 * Handles special cases: localhost, IP addresses, dots → dashes
 */
export const encodeDomainToCookieSuffix = (domain: string): string => {
  // Remove leading dot if present
  const domainWithoutDot = domain.replace(/^\./, '');

  // Handle localhost and IP addresses - use as-is but replace dots/colons with dashes
  if (domainWithoutDot === 'localhost' || /^(\d{1,3}\.){3}\d{1,3}$/.test(domainWithoutDot) || domainWithoutDot.includes('vercel')) {
    return domainWithoutDot.replace(/[.:]/g, '-');
  }

  // For regular domains, replace dots with dashes
  return domainWithoutDot.replace(/\./g, '-');
};

/**
 * Get root domain for cookie domain attribute
 * Returns domain like ".qa.lendi.pl" or ".lendi.pl" for setting cookie domain
 */
export const getRootDomain = (): string => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) || hostname.includes('vercel')) {
    return hostname;
  }
  const parts = hostname.split('.');
  // Special case: if hostname ends with .lendi.pl and has exactly 3 parts (e.g., platforma.lendi.pl),
  // return .lendi.pl instead of .platforma.lendi.pl
  if (parts.length === 3 && parts.slice(-2).join('.') === 'lendi.pl') {
    return `.lendi.pl`;
  }
  if (parts.length >= 3) {
    return `.${parts.slice(-3).join('.')}`; // e.g., .qa.lendi.pl
  }
  if (parts.length > 1) {
    return `.${parts.slice(-2).join('.')}`; // e.g., .lendi.pl
  }
  return hostname;
};

/**
 * Get shared domain for cookies that should be shared across all subdomains
 * Returns ".lendi.pl" for production domains, or hostname for localhost/IP/vercel
 */
export const getSharedDomain = (): string => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname) || hostname.includes('vercel')) {
    return hostname;
  }
  // For any lendi.pl domain, return .lendi.pl to share across all subdomains
  const parts = hostname.split('.');
  if (parts.length >= 2 && parts.slice(-2).join('.') === 'lendi.pl') {
    return `.lendi.pl`;
  }
  // Fallback to root domain if not lendi.pl
  if (parts.length > 1) {
    return `.${parts.slice(-2).join('.')}`;
  }
  return hostname;
};

